<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useLangStore } from "@/stores/lang.js";
import { useSideRailsStore } from "@/stores/sideRails.js";
import { useMonitorStore } from "@/stores/monitor.js";
import { thumbSrc, onThumbError } from "@/api/client.js";
import { copyText, shortAddr, flapUrl, formatAgo } from "@/lib/ui.js";

const route = useRoute();
const lang = useLangStore();
const rails = useSideRailsStore();
const monitor = useMonitorStore();
const isMonitorPage = computed(() => route.name === "monitor");
const deploysLoading = computed(() => isMonitorPage.value ? monitor.loading : rails.deploysLoading);

// Start collapsed before the first render; page-specific preferences are read below.
const gainersCollapsed = ref(true);
const deploysCollapsed = ref(true);
const broken = ref({});

const isBrowsePage = computed(() => route.name === "home" || route.name === "shop");

const showRails = computed(() => {
  const n = route.name;
  if (!n) return false;
  if (n === "usage-guide") return false;
  // 部署 / 市值 / Mint 独立操作区：隐藏双侧栏，并停用对应请求和轮询。
  if (n === "deploy-bbk-v3" || n === "deploy-stock") return false;
  if (n === "market" || n === "tasks" || n === "token-detail" || n === "admin-health") return false;
  if (n === "mint-live" || n === "mint-create" || n === "mint-detail") return false;
  if (n === "mint-monitor") return false;
  return true;
});

function clearRailBodyClasses() {
  const b = document.body;
  b.classList.remove(
    "has-gainers",
    "has-deploys",
    "gainers-collapsed",
    "deploys-collapsed"
  );
}

function isPhoneLike() {
  try {
    if (typeof matchMedia !== "function") return false;
    // 窄屏一律当手机：不依赖 coarse（DevTools / 部分安卓会报 fine）
    if (matchMedia("(max-width: 720px)").matches) return true;
    const sw = window.screen?.width || 0;
    return sw > 0 && sw <= 480;
  } catch {
    return false;
  }
}

/** <1440：双侧栏展开会盖住 --max 主栏，默认折叠 */
function isRailCrowded() {
  try {
    if (typeof matchMedia !== "function") return isPhoneLike();
    return matchMedia("(max-width: 1439px)").matches || isPhoneLike();
  } catch {
    return isPhoneLike();
  }
}

function collapseRailsForViewport() {
  if (!isRailCrowded()) return false;
  let changed = false;
  if (!gainersCollapsed.value) {
    gainersCollapsed.value = true;
    changed = true;
  }
  if (!deploysCollapsed.value) {
    deploysCollapsed.value = true;
    changed = true;
  }
  if (changed) syncBody();
  return changed;
}

function restoreRailPreference() {
  // Catalog browsing starts quiet without overwriting the monitoring preference.
  gainersCollapsed.value = true;
  deploysCollapsed.value = true;
  if (!route.name || isBrowsePage.value || isRailCrowded()) return;
  try {
    gainersCollapsed.value = localStorage.getItem("flap_gainers_side") !== "1";
    deploysCollapsed.value = localStorage.getItem("flap_deploys_side") !== "1";
  } catch {
    /* Local storage is optional. */
  }
}

function saveRailPreference() {
  if (isBrowsePage.value) return;
  try {
    localStorage.setItem("flap_gainers_side", gainersCollapsed.value ? "0" : "1");
    localStorage.setItem("flap_deploys_side", deploysCollapsed.value ? "0" : "1");
  } catch {
    /* Local storage is optional. */
  }
}

function fmtChange(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const body =
    abs >= 1000 ? abs.toFixed(0) : abs >= 100 ? abs.toFixed(1) : abs.toFixed(2);
  return (v > 0 ? "+" : v < 0 ? "−" : "") + body + "%";
}

function fmtAgo(ms) {
  return formatAgo(ms, lang.t);
}

function markBroken(token) {
  broken.value = { ...broken.value, [token]: true };
}

function letter(it) {
  return String(it.symbol || it.name || "?").slice(0, 1).toUpperCase();
}

function syncBody() {
  const b = document.body;
  const on = showRails.value;
  rails.enabled = on;
  if (!on) {
    clearRailBodyClasses();
    b.classList.toggle("market-shell", route.name === "market");
    return;
  }
  b.classList.toggle("has-gainers", true);
  b.classList.toggle("has-deploys", true);
  b.classList.toggle("gainers-collapsed", gainersCollapsed.value);
  b.classList.toggle("deploys-collapsed", deploysCollapsed.value);
  b.classList.toggle("market-shell", route.name === "market");
}

function toggleGainers() {
  gainersCollapsed.value = !gainersCollapsed.value;
  if (!gainersCollapsed.value) rails.fetchGainers({ soft: true });
  if (isRailCrowded() && !gainersCollapsed.value) deploysCollapsed.value = true;
  saveRailPreference();
  syncBody();
}

function toggleDeploys() {
  deploysCollapsed.value = !deploysCollapsed.value;
  if (isRailCrowded() && !deploysCollapsed.value) gainersCollapsed.value = true;
  saveRailPreference();
  syncBody();
}

async function onCopy(token, label) {
  await copyText(token, lang.t("已复制 ", "Copied ") + (label || shortAddr(token)));
}

let gTimer = 0;
let dTimer = 0;
let mqNarrow = null;
let mqCrowd = null;
let mounted = false;

function railsPollMs() {
  return document.hidden ? 120000 : 30000;
}

function deployPollMs() {
  return document.hidden ? 60000 : 30000;
}

function scheduleRailsPoll() {
  if (gTimer) clearInterval(gTimer);
  if (dTimer) clearInterval(dTimer);
  if (!showRails.value) return;
  gTimer = window.setInterval(() => rails.fetchGainers({ soft: true }), railsPollMs());
  // MonitorView owns list polling, pause, and fresh-token alerts on this route.
  if (!isMonitorPage.value) {
    dTimer = window.setInterval(() => rails.fetchDeploys({ soft: true }), deployPollMs());
  }
}

function refreshRails(force = false) {
  if (isMonitorPage.value) return rails.fetchGainers({ soft: !force });
  return rails.refreshAll(force);
}

function onRailsVisChange() {
  if (!document.hidden && showRails.value) refreshRails(false);
  scheduleRailsPoll();
}

function onNarrowChange() {
  if (!mqNarrow?.matches) return;
  if (!gainersCollapsed.value && !deploysCollapsed.value) {
    deploysCollapsed.value = true;
    syncBody();
  }
}

function onCrowdChange() {
  if (mqCrowd?.matches) collapseRailsForViewport();
}

watch(
  () => route.name,
  () => {
    const wasEnabled = rails.enabled;
    restoreRailPreference();
    syncBody();
    if (!mounted) return;
    scheduleRailsPoll();
    if (showRails.value && !wasEnabled) refreshRails(false);
  },
  { immediate: true }
);

watch([() => monitor.rows, isMonitorPage], () => {
  if (isMonitorPage.value) rails.syncDeploysFromMonitor();
}, { immediate: true });

onMounted(() => {
  mounted = true;
  syncBody();
  rails.hydrateFromCache();
  if (isMonitorPage.value) rails.syncDeploysFromMonitor();
  if (showRails.value) {
    refreshRails(true);
    scheduleRailsPoll();
  }
  document.addEventListener("visibilitychange", onRailsVisChange);
  try {
    mqNarrow = window.matchMedia("(max-width: 720px)");
    mqNarrow.addEventListener?.("change", onNarrowChange);
    mqCrowd = window.matchMedia("(max-width: 1439px)");
    mqCrowd.addEventListener?.("change", onCrowdChange);
  } catch {
    /* ignore */
  }
});

onUnmounted(() => {
  mounted = false;
  if (gTimer) clearInterval(gTimer);
  if (dTimer) clearInterval(dTimer);
  document.removeEventListener("visibilitychange", onRailsVisChange);
  try {
    mqNarrow?.removeEventListener?.("change", onNarrowChange);
    mqCrowd?.removeEventListener?.("change", onCrowdChange);
  } catch {
    /* ignore */
  }
  document.body.classList.remove(
    "has-gainers",
    "has-deploys",
    "gainers-collapsed",
    "deploys-collapsed",
    "market-shell"
  );
});
</script>

<template>
  <template v-if="showRails">
    <aside
      id="deploys-rail"
      class="deploys-rail"
      :class="{ 'is-collapsed': deploysCollapsed }"
      :aria-label="lang.t('刚刚部署侧栏', 'Latest deploys rail')"
    >
      <div class="gainers-label">
        <span class="gainers-pulse is-new" aria-hidden="true" />
        <span class="gainers-label-text">{{ lang.t("刚刚部署", "Just deployed") }}</span>
        <span v-if="rails.deploysCount" class="gainers-count">{{ rails.deploysCount }}</span>
        <button
          type="button"
          class="gainers-toggle"
          :aria-expanded="!deploysCollapsed"
          :aria-label="deploysCollapsed ? lang.t('展开最新部署', 'Show latest deploys') : lang.t('收起最新部署', 'Hide latest deploys')"
          :title="deploysCollapsed ? lang.t('展开侧栏', 'Expand') : lang.t('收起侧栏', 'Collapse')"
          @click="toggleDeploys"
        >
          {{ deploysCollapsed ? "‹" : "›" }}
        </button>
      </div>
      <div class="gainers-viewport">
        <div v-if="!rails.deploys.length" class="gainers-skeleton">
          {{
            deploysLoading
              ? lang.t("加载刚刚部署…", "Loading deploys…")
              : lang.t("暂无刚刚部署 · 稍后自动更新", "No recent deploys yet")
          }}
        </div>
        <div v-else class="gainers-track">
          <div
            v-for="(it, i) in rails.deploys"
            :key="it.token"
            class="gainers-item is-latest"
            :class="{ 'is-listed': it.listed }"
            :data-token="it.token"
          >
            <span class="gainers-rank" aria-hidden="true">{{
              String(i + 1).padStart(2, "0")
            }}</span>
            <button
              type="button"
              class="gainers-main"
              :title="(it.symbol || it.name) + ' · ' + shortAddr(it.token)"
              @click="onCopy(it.token, it.symbol || it.name)"
            >
              <img
                v-if="it.image && !broken[it.token]"
                class="gainers-avatar"
                :src="thumbSrc(it.image)"
                alt=""
                loading="lazy"
                decoding="async"
                @error="(e) => onThumbError(e, it.image)"
              />
              <span v-else class="gainers-mark">{{ letter(it) }}</span>
              <span class="gainers-meta">
                <span class="gainers-sym">{{
                  it.symbol || it.name || shortAddr(it.token)
                }}</span>
                <span class="gainers-sub">
                  {{ fmtAgo(it.time)
                  }}{{ it.factoryName ? " · " + it.factoryName : "" }}
                </span>
              </span>
            </button>
            <a
              class="gainers-go"
              :href="flapUrl(it.token)"
              target="_blank"
              rel="noopener noreferrer"
              title="Flap"
            >Flap</a>
          </div>
        </div>
      </div>
    </aside>

    <aside
      id="gainers-rail"
      class="gainers-rail"
      :class="{ 'is-collapsed': gainersCollapsed }"
      :aria-label="lang.t('24小时涨幅侧栏', '24h gainers rail')"
    >
      <div class="gainers-label">
        <span class="gainers-pulse" aria-hidden="true" />
        <span class="gainers-label-text">{{ lang.t("已毕业涨幅", "Listed gainers") }}</span>
        <span v-if="rails.gainersCount" class="gainers-count">{{ rails.gainersCount }}</span>
        <button
          type="button"
          class="gainers-toggle"
          :aria-expanded="!gainersCollapsed"
          :aria-label="gainersCollapsed ? lang.t('展开已毕业涨幅', 'Show listed gainers') : lang.t('收起已毕业涨幅', 'Hide listed gainers')"
          :title="gainersCollapsed ? lang.t('展开侧栏', 'Expand') : lang.t('收起侧栏', 'Collapse')"
          @click="toggleGainers"
        >
          {{ gainersCollapsed ? "›" : "‹" }}
        </button>
      </div>
      <div class="gainers-viewport">
        <div v-if="!rails.gainers.length" class="gainers-skeleton">
          {{
            rails.gainersLoading
              ? lang.t("加载已毕业涨幅…", "Loading gainers…")
              : rails.gainersUnavailable
                ? lang.t("涨幅数据暂不可用，正在重试…", "Gainers unavailable, retrying…")
                : lang.t("暂无涨幅数据", "No gainers yet")
          }}
        </div>
        <div v-else class="gainers-track">
          <p v-if="rails.gainersStale" class="gainers-refresh-note" role="status">
            {{ lang.t("后台自动刷新中，保留上次涨幅数据", "Refreshing in the background; showing the last known changes") }}
          </p>
          <div
            v-for="(it, i) in rails.gainers"
            :key="it.token"
            class="gainers-item"
            :class="{
              'is-up': it.change24h > 0,
              'is-down': it.change24h < 0,
              'is-hot': it.change24h >= 50,
            }"
            :data-token="it.token"
          >
            <span class="gainers-rank" aria-hidden="true">{{
              String(i + 1).padStart(2, "0")
            }}</span>
            <button
              type="button"
              class="gainers-main"
              :title="(it.symbol || 'TOKEN') + ' · ' + shortAddr(it.token)"
              :aria-label="lang.t('复制合约', 'Copy contract') + ': ' + (it.symbol || shortAddr(it.token))"
              @click="onCopy(it.token, it.symbol)"
            >
              <img
                v-if="it.image && !broken[it.token]"
                class="gainers-avatar"
                :src="thumbSrc(it.image)"
                alt=""
                loading="lazy"
                decoding="async"
                @error="(e) => onThumbError(e, it.image)"
              />
              <span v-else class="gainers-mark">{{ letter(it) }}</span>
              <span class="gainers-meta">
                <span class="gainers-sym">{{ it.symbol || "TOKEN" }}</span>
                <span class="gainers-chg">
                  <span class="gainers-arrow" aria-hidden="true">{{
                    it.change24h > 0 ? "▲" : it.change24h < 0 ? "▼" : "•"
                  }}</span>
                  {{ fmtChange(it.change24h) }}
                  <span v-if="it.stale" :title="fmtAgo(it.updatedAt * 1000)">
                    · {{ lang.t("待更新", "Cached") }}
                  </span>
                </span>
              </span>
            </button>
            <a
              class="gainers-go"
              :href="flapUrl(it.token)"
              target="_blank"
              rel="noopener noreferrer"
              title="Flap"
            >Flap</a>
          </div>
        </div>
      </div>
    </aside>
  </template>
</template>
